// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerToolBar.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Report Designer ToolBar component
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import AuthorizationValidator from 'share/components/hoc/Izenda.AuthorizationValidator';
import AuthorizationConstants from 'logic/data/Izenda.AuthorizationConstants';
import Button from 'share/components/controls/Izenda.Button';
import ActionButton from 'share/components/controls/Izenda.ActionButton';
import IconMenu from 'share/components/controls/Izenda.IconMenu';
import Alerts from './Toolbar/Izenda.ReportDesignerAlerts';
import AddReportPartButton from './Toolbar/Izenda.AddReportPartButton';
import SaveReportDesignerButton from './Toolbar/Izenda.SaveReportDesignerButton';
import constants from 'logic/data/Izenda.ReportDesignerConstants';
import ReportButtons from 'modules/Reports/Buttons/Izenda.ReportButtons';
import connect from 'share/enhancers/Izenda.Connect';
import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
import actions from './Izenda.ReportDesignerToolBarActions';
import { isNil, get } from 'lodash/fp';

const ACCESS_RIGHT = AuthorizationConstants.ACCESS_RIGHT;
const ExportBtn = AuthorizationValidator()((props: any) => <ReportButtons.ExportReportButton {...props} />);

@connect(actions)
class ReportDesignerToolBar extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired,
    fieldsStore: PropTypes.object.isRequired,
    pageName: PropTypes.string.isRequired,
    onUpdateResults: PropTypes.func.isRequired,
    onAddReportPart: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onSaveAs: PropTypes.func.isRequired,
    onCancelReport: PropTypes.func.isRequired
  };

  componentDidMount() {
    this.props.resetAlertDetails(this.props.store);
  }

  /**
   * Alert dismiss handler.
   */
  onDismiss() {
    // We need to reset "needUpdateResult" property, otherwise alert won't appear after being closed.
    const reportFilters = get('fieldsStore.reportFilters', this.props);
    if (!isNil(reportFilters))
      reportFilters.needUpdateResult = false;
  }

  render() {
    const props = this.props;
    const {
      store,
      store: { model, canSaveGlobalReport },
      localization,
      authorization,
      pageName
    } = props;
    const accessPriority = model.accessPriority || ACCESS_RIGHT.FullAccess;
    const canSaveAs = store.canSaveAs && props.checkPermissionOfSaveAsButton(props);
    const onAddReportPart = props.onAddReportPart;
    const onSave = props.onSave;
    const onSaveAs = props.onSaveAs;
    const onPreview = props.onPreview;
    const onCancelReport = props.onCancelReport;
    const hasExportLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Exporting);
    const hasReportTemplateLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.ReportTemplateComponent);

    const allowAdd = pageName === constants.PAGE_NAME.FIELDS;
    const doesShowSaveButton = props.checkPermissionOfSaveButton(props);
    const onDismiss = this.onDismiss.bind(this);

    const { message, alertType, key: alertKey, success, errors, showType, type } = store.alertDetails;

    return (
      <div className="izenda-Wrapper-header">
        <div className="u_inlineBlock u_fullWidth">
          <IconMenu key={pageName} />

          <div className="izenda-Alert-container">
            <Alerts
              message={message}
              alertType={alertType}
              alertKey={alertKey}
              success={success}
              errors={errors}
              type={type}
              showType={showType}
              onDismiss={onDismiss}
            />
          </div>

          <div className="izenda-ActionBarRight">
            <ActionButton>
              {props.checkDisplayShowHideHeaderFooter(pageName, store) && (
                <Button
                  className="btn-report-default"
                  onClick={props.onDisplayShowHideHeaderFooter}
                  label={props.getShowHideHeaderFooterLabel(localization, store)}
                />
              )}

              {hasExportLicense && (
                <ExportBtn
                  reportDesignerMode
                  name={model.reportName}
                  store={store}
                  print={store.printReport}
                  export={store.exportReport}
                  id={model.reportId}
                  keys={[AuthorizationConstants.PERMISSION_KEY.ReportsActionsExporting]}
                />
              )}

              <AddReportPartButton allowAdd={allowAdd} onAdd={onAddReportPart} />

              {doesShowSaveButton && (
                <SaveReportDesignerButton
                  canSaveGlobalReport={canSaveGlobalReport}
                  canSaveAs={canSaveAs}
                  onSave={onSave}
                  onSaveAs={onSaveAs}
                  version={model.version}
                  keepDisplay
                  accessPriority={accessPriority}
                  disabled={ (!hasReportTemplateLicense) && store.disableReportType}
                />
              )}

              <Button className='btn-report-default'
                      onClick={onCancelReport}
                      label={localization.getText('COMMON_LABEL_CANCEL')}
                      keepDisplay />

              <Button className='btn-report-default'
                      onClick={onPreview}
                      label={localization.getText('COMMON_LABEL_REPORT_VIEWER')} />

            </ActionButton>
          </div>
        </div>
      </div>
    );
  }
}

export default ReportDesignerToolBar;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Izenda.ReportDesignerToolBar.js